import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { ZoomIn, ZoomOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Object-URL / data-URL of the image to crop (already HEIC-decoded). */
  imageSrc: string | null;
  /** Brand accent for the Save button + zoom slider. */
  accent: string;
  /** Called with the final cropped, downscaled JPEG data-URL. */
  onSave: (dataUrl: string) => void;
  /** Square output edge length in px. */
  outputSize?: number;
}

/**
 * Load an image element from a URL. Resolves once decoded so canvas draws
 * are safe. The URL is created from a local blob, so the canvas stays
 * untainted and `toDataURL` works.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

/**
 * Render the chosen crop region to a square canvas at `outputSize` and return
 * a JPEG data-URL. `pixelCrop` is in natural-image pixels (react-easy-crop's
 * croppedAreaPixels), so it maps directly onto drawImage's source rect.
 */
async function getCroppedDataUrl(
  imageSrc: string,
  pixelCrop: Area,
  outputSize: number,
  quality = 0.9,
): Promise<string> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not available");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize,
  );
  return canvas.toDataURL("image/jpeg", quality);
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageSrc,
  accent,
  onSave,
  outputSize = 512,
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixels(areaPixels);
  }, []);

  // Reset transform each time a new image is opened so we don't inherit the
  // previous photo's zoom/position.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixels(null);
    }
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (!imageSrc || !pixels) return;
    setSaving(true);
    try {
      const dataUrl = await getCroppedDataUrl(imageSrc, pixels, outputSize);
      onSave(dataUrl);
      handleOpenChange(false);
    } catch {
      toast.error("Could not crop that image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Position photo</DialogTitle>
          <DialogDescription>
            Drag to reposition and use the slider to zoom. The circle shows how the headshot
            will be cropped.
          </DialogDescription>
        </DialogHeader>

        {/* Crop stage */}
        <div className="relative w-full h-72 bg-muted rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={1}
              maxZoom={4}
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        {/* Zoom control */}
        <div className="flex items-center gap-3 px-1">
          <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full cursor-pointer"
            style={{ accentColor: accent }}
            aria-label="Zoom"
          />
          <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!pixels || saving}
            style={{ backgroundColor: accent, color: "white" }}
          >
            {saving ? "Saving…" : "Use Photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
